import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Scale, FileText, AlertCircle, Plus, ExternalLink, Copyright } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface ContentAttribution {
  id: string;
  node_id: string;
  attribution_type: 'original' | 'quoted' | 'referenced' | 'derived';
  source_title?: string;
  source_author?: string;
  source_url?: string;
  source_publication?: string;
  license_type?: string;
  copyright_notice?: string;
  permission_granted: boolean;
  verification_status: 'unverified' | 'verified' | 'disputed';
  added_by?: string;
  created_at: string;
  updated_at: string;
}

const LegalComplianceTools = () => {
  const [attributions, setAttributions] = useState<ContentAttribution[]>([]);
  const [loading, setLoading] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const { toast } = useToast();

  const [newAttribution, setNewAttribution] = useState({
    node_id: '',
    attribution_type: 'referenced' as const,
    source_title: '',
    source_author: '',
    source_url: '',
    source_publication: '',
    license_type: '',
    copyright_notice: '',
    permission_granted: false,
  });

  const [attributionFilter, setAttributionFilter] = useState<'all' | 'required' | 'missing' | 'disputed'>('all');

  useEffect(() => {
    fetchAttributions();
  }, []);

  const fetchAttributions = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('content_attribution')
        .select(`
          *,
          nodes!inner(title)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      // Type assertion to ensure compatibility with our interface
      setAttributions((data || []) as ContentAttribution[]);
    } catch (error: any) {
      console.error('Error fetching attributions:', error);
      toast({
        title: "Error",
        description: "Failed to load content attributions",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const addAttribution = async () => {
    if (!newAttribution.node_id || !newAttribution.attribution_type) {
      toast({
        title: "Error",
        description: "Please fill in required fields",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase
        .from('content_attribution')
        .insert(newAttribution);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Attribution added successfully",
      });

      setIsAddDialogOpen(false);
      setNewAttribution({
        node_id: '',
        attribution_type: 'referenced',
        source_title: '',
        source_author: '',
        source_url: '',
        source_publication: '',
        license_type: '',
        copyright_notice: '',
        permission_granted: false,
      });
      
      fetchAttributions();
    } catch (error: any) {
      console.error('Error adding attribution:', error);
      toast({
        title: "Error",
        description: "Failed to add attribution",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateVerificationStatus = async (
    attributionId: string, 
    status: 'unverified' | 'verified' | 'disputed'
  ) => {
    try {
      const { error } = await supabase
        .from('content_attribution')
        .update({ verification_status: status })
        .eq('id', attributionId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Verification status updated",
      });

      fetchAttributions();
    } catch (error: any) {
      console.error('Error updating verification status:', error);
      toast({
        title: "Error",
        description: "Failed to update verification status",
        variant: "destructive",
      });
    }
  };

  const getAttributionTypeBadge = (type: string) => {
    const variants: Record<string, any> = {
      original: 'default',
      quoted: 'secondary',
      referenced: 'outline',
      derived: 'default'
    };

    return <Badge variant={variants[type]}>{type}</Badge>;
  };

  const getVerificationBadge = (status: string) => {
    const variants: Record<string, any> = {
      unverified: 'outline',
      verified: 'default',
      disputed: 'destructive'
    };

    const colors: Record<string, string> = {
      unverified: 'text-black',
      verified: 'text-green-600',
      disputed: 'text-red-600'
    };

    return (
      <Badge variant={variants[status]} className={colors[status]}>
        {status}
      </Badge>
    );
  };

  // Helper: empty or missing required fields?
  function isIncomplete(a: ContentAttribution) {
    return !a.source_title || !a.source_author || !(a.source_url || a.source_publication || a.license_type);
  }

  const filteredAttributions = attributions.filter(a => {
    if (attributionFilter === 'all') return true;
    if (attributionFilter === 'required') return !!isIncomplete(a) && a.verification_status !== 'disputed';
    if (attributionFilter === 'missing') return !a.source_title || !a.source_author;
    if (attributionFilter === 'disputed') return a.verification_status === 'disputed';
    return true;
  });

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Scale className="h-5 w-5" />
              Legal Compliance & Attribution
            </CardTitle>
            <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Attribution
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Add Content Attribution</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="node_id">Content ID *</Label>
                    <Input
                      id="node_id"
                      value={newAttribution.node_id}
                      onChange={(e) => setNewAttribution(prev => ({ ...prev, node_id: e.target.value }))}
                      placeholder="Enter content/node ID"
                    />
                  </div>

                  <div>
                    <Label htmlFor="attribution_type">Attribution Type *</Label>
                    <Select
                      value={newAttribution.attribution_type}
                      onValueChange={(value: any) => setNewAttribution(prev => ({ ...prev, attribution_type: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="original">Original</SelectItem>
                        <SelectItem value="quoted">Quoted</SelectItem>
                        <SelectItem value="referenced">Referenced</SelectItem>
                        <SelectItem value="derived">Derived</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="source_title">Source Title</Label>
                    <Input
                      id="source_title"
                      value={newAttribution.source_title}
                      onChange={(e) => setNewAttribution(prev => ({ ...prev, source_title: e.target.value }))}
                      placeholder="Title of the source material"
                    />
                  </div>

                  <div>
                    <Label htmlFor="source_author">Source Author</Label>
                    <Input
                      id="source_author"
                      value={newAttribution.source_author}
                      onChange={(e) => setNewAttribution(prev => ({ ...prev, source_author: e.target.value }))}
                      placeholder="Author of the source material"
                    />
                  </div>

                  <div>
                    <Label htmlFor="source_url">Source URL</Label>
                    <Input
                      id="source_url"
                      value={newAttribution.source_url}
                      onChange={(e) => setNewAttribution(prev => ({ ...prev, source_url: e.target.value }))}
                      placeholder="https://..."
                    />
                  </div>

                  <div>
                    <Label htmlFor="license_type">License Type</Label>
                    <Input
                      id="license_type"
                      value={newAttribution.license_type}
                      onChange={(e) => setNewAttribution(prev => ({ ...prev, license_type: e.target.value }))}
                      placeholder="e.g., CC BY-SA 4.0, MIT, etc."
                    />
                  </div>

                  <div>
                    <Label htmlFor="copyright_notice">Copyright Notice</Label>
                    <Textarea
                      id="copyright_notice"
                      value={newAttribution.copyright_notice}
                      onChange={(e) => setNewAttribution(prev => ({ ...prev, copyright_notice: e.target.value }))}
                      placeholder="Copyright notice or attribution text"
                      rows={3}
                    />
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="permission_granted"
                      checked={newAttribution.permission_granted}
                      onChange={(e) => setNewAttribution(prev => ({ ...prev, permission_granted: e.target.checked }))}
                      className="rounded"
                    />
                    <Label htmlFor="permission_granted">Permission granted for use</Label>
                  </div>

                  <Button onClick={addAttribution} disabled={loading} className="w-full">
                    Add Attribution
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="attributions" className="space-y-4">
            <TabsList>
              <TabsTrigger value="attributions">Content Attributions</TabsTrigger>
              <TabsTrigger value="compliance">Compliance Monitor</TabsTrigger>
              <TabsTrigger value="terms">Terms Monitoring</TabsTrigger>
            </TabsList>

            <TabsContent value="attributions">
              {/* Attribution compliance filter control */}
              <div className="my-2 flex gap-2 items-center">
                <span className="text-xs font-bold">Filter:</span>
                <Button size="sm" variant={attributionFilter === 'all' ? "default" : "outline"} onClick={() => setAttributionFilter('all')}>All</Button>
                <Button size="sm" variant={attributionFilter === 'required' ? "destructive" : "outline"} onClick={() => setAttributionFilter('required')}>Incomplete Required</Button>
                <Button size="sm" variant={attributionFilter === 'missing' ? "secondary" : "outline"} onClick={() => setAttributionFilter('missing')}>Missing Info</Button>
                <Button size="sm" variant={attributionFilter === 'disputed' ? "secondary" : "outline"} onClick={() => setAttributionFilter('disputed')}>Disputed</Button>
              </div>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Content</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>License</TableHead>
                      <TableHead>Permission</TableHead>
                      <TableHead>Compliance</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAttributions.map((attribution) => (
                      <TableRow key={attribution.id}>
                        <TableCell>
                          <div className="font-medium">
                            {(attribution as any).nodes?.title || 'Unknown Content'}
                          </div>
                          <div className="text-xs text-black">
                            ID: {attribution.node_id.substring(0, 8)}...
                          </div>
                        </TableCell>
                        <TableCell>{getAttributionTypeBadge(attribution.attribution_type)}</TableCell>
                        <TableCell>
                          <div className="max-w-[200px]">
                            {attribution.source_title && (
                              <div className="font-medium truncate">{attribution.source_title}</div>
                            )}
                            {attribution.source_author && (
                              <div className="text-sm text-black">by {attribution.source_author}</div>
                            )}
                            {attribution.source_url && (
                              <a 
                                href={attribution.source_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-xs text-black hover:underline flex items-center gap-1"
                              >
                                <ExternalLink className="h-3 w-3" />
                                Source
                              </a>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {attribution.license_type ? (
                            <Badge variant="outline">{attribution.license_type}</Badge>
                          ) : (
                            <span className="text-black">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={attribution.permission_granted ? 'default' : 'destructive'}>
                            {attribution.permission_granted ? 'Granted' : 'Not Granted'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {/* Compliance badge: required, missing, verified, disputed */}
                          {isIncomplete(attribution) ? (
                            <Badge variant="destructive" className="uppercase">Incomplete</Badge>
                          ) : attribution.verification_status === "verified" ? (
                            <Badge variant="default">Verified</Badge>
                          ) : attribution.verification_status === "disputed" ? (
                            <Badge variant="outline" className="text-red-600 border-red-400">Disputed</Badge>
                          ) : (
                            <Badge variant="outline" className="text-black border-sky-400">Unverified</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            {/* Admin actions: verify/dispute */}
                            {attribution.verification_status === 'unverified' && !isIncomplete(attribution) && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => updateVerificationStatus(attribution.id, 'verified')}
                              >
                                Verify
                              </Button>
                            )}
                            {attribution.verification_status === 'verified' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => updateVerificationStatus(attribution.id, 'disputed')}
                              >
                                Dispute
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredAttributions.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-black py-8">
                          No attributions found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            <TabsContent value="compliance">
              <div className="grid gap-4 md:grid-cols-2">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Copyright className="h-5 w-5" />
                      Copyright Compliance
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span>Total Attributions:</span>
                        <Badge>{attributions.length}</Badge>
                      </div>
                      <div className="flex justify-between">
                        <span>Verified:</span>
                        <Badge variant="default">
                          {attributions.filter(a => a.verification_status === 'verified').length}
                        </Badge>
                      </div>
                      <div className="flex justify-between">
                        <span>Needs Permission:</span>
                        <Badge variant="destructive">
                          {attributions.filter(a => !a.permission_granted).length}
                        </Badge>
                      </div>
                      <div className="flex justify-between">
                        <span>Disputed:</span>
                        <Badge variant="destructive">
                          {attributions.filter(a => a.verification_status === 'disputed').length}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <AlertCircle className="h-5 w-5" />
                      Compliance Alerts
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {attributions.filter(a => !a.permission_granted).length > 0 && (
                        <div className="p-2 bg-red-50 border border-red-200 rounded">
                          <div className="text-sm text-red-800">
                            {attributions.filter(a => !a.permission_granted).length} content items lack proper permission
                          </div>
                        </div>
                      )}
                      {attributions.filter(a => a.verification_status === 'unverified').length > 0 && (
                        <div className="p-2 bg-white border border-sky-200 rounded">
                          <div className="text-sm text-black">
                            {attributions.filter(a => a.verification_status === 'unverified').length} attributions need verification
                          </div>
                        </div>
                      )}
                      {attributions.filter(a => a.verification_status === 'disputed').length > 0 && (
                        <div className="p-2 bg-red-50 border border-red-200 rounded">
                          <div className="text-sm text-red-800">
                            {attributions.filter(a => a.verification_status === 'disputed').length} attributions are disputed
                          </div>
                        </div>
                      )}
                      {attributions.every(a => a.permission_granted && a.verification_status === 'verified') && (
                        <div className="p-2 bg-white border border-green-200 rounded">
                          <div className="text-sm text-green-800">
                            All attributions are compliant
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="terms">
              <Card>
                <CardHeader>
                  <CardTitle>Terms of Service Monitoring</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center text-black py-8">
                    <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>Terms monitoring features will be implemented here</p>
                    <p className="text-sm">This will include automated scanning for policy violations</p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default LegalComplianceTools;
